/**
 * A switch that can be thrown inside the installed app, where there is no address bar.
 *
 * Leo, 2026-09-15, on being handed a `?diag=1` URL: *"Isn't this going to get us data from a
 * browser and not the home installed app? We should just have a debug button somewhere
 * inconspicuous while in heavy dev mode in the app itself."*
 *
 * He is right, and the mistake is worth naming because it would have produced a fourth
 * confident wrong fix. **A query parameter can only be typed where there is somewhere to type
 * it**, and the installed app has no address bar — `start_url` is `/` and it opens there. So
 * `?diag=1` reaches Safari, which is the one environment the bug does *not* live in: the whole
 * difficulty is that standalone iOS treats the viewport differently from a browser tab.
 * Readings from the wrong environment are worse than none, because they look like evidence.
 *
 * Kept in `localStorage` rather than in the URL for the same reason it is toggled by a
 * gesture: it has to survive a navigation, a relaunch from the home screen, and an app that
 * never shows a URL at all.
 */

const KEY = 'nabu.debug';

/**
 * Whether the diagnostics are on.
 *
 * Every read is wrapped: `localStorage` throws outright in a private window on some browsers,
 * and a debug switch must never be the reason an app fails to render. Absent, unreadable and
 * "off" are one answer here, which is the only one of the three that matters.
 */
export function debugOn(): boolean {
  try {
    if (window.localStorage.getItem(KEY) === '1') return true;
  } catch {
    // Storage is unavailable. Fall through to the URL, which still works in a browser tab.
  }
  try {
    return new URLSearchParams(window.location.search).get('diag') === '1';
  } catch {
    return false;
  }
}

/** Turns it on or off, and answers with what it now is so a caller can say so. */
export function setDebug(on: boolean): boolean {
  try {
    if (on) window.localStorage.setItem(KEY, '1');
    else window.localStorage.removeItem(KEY);
    return on;
  } catch {
    /*
     * Nothing was stored, so nothing changed, and saying `on` would be a lie the next screen
     * would contradict. A gesture that silently does nothing is confusing; one that reports
     * the truth lets the caller say "not available here".
     */
    return false;
  }
}

/**
 * How long a press has to be held to count, in milliseconds.
 *
 * Long enough that it is never reached by somebody tapping the wordmark to go home — a tap is
 * under two hundred milliseconds — and short enough to be discoverable by anybody who has been
 * told it exists. A tap *count* was the alternative and is worse here: the wordmark is a link,
 * so five rapid taps would navigate five times on the way to the fifth.
 */
export const DEBUG_HOLD_MS = 900;
